<template>
  <div class="filter-wrapper">
    <h2 class="section-title">GIF 滤镜</h2>

    <!-- 加载提示 -->
    <loading-tip 
      :visible="isLoading" 
      :text="loadingText" 
      :subtext="loadingSubtext"
      :progress="loadingProgress"
    />

    <!-- 上传 GIF -->
    <label v-if="!hasGif" class="drop-area" :class="{ hover: isDragging }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
      拖拽或点击上传 GIF
      <div class="size-hint">{{ isMobile ? '建议不超过 8MB' : '建议不超过 20MB' }}</div>
      <input type="file" ref="gifInput" accept="image/gif" @change="handleFileChange">
    </label>

    <!-- 预览区域 -->
    <div v-if="hasGif" class="preview-section">
      <div class="preview-header">
        <span class="preview-title">滤镜预览</span>
        <button class="btn-reselect" @click="resetAll">重新选择</button>
      </div>
      <div class="preview-canvas-wrapper">
        <canvas ref="previewCanvas" class="preview-canvas"></canvas>
      </div>
    </div>

    <!-- 滤镜选项 -->
    <div v-if="hasGif" class="filter-options">
      <div class="option-title">预设滤镜</div>
      
      <div class="filter-grid">
        <div 
          v-for="filter in filters" 
          :key="filter.name"
          class="filter-item"
          :class="{ active: currentFilter === filter.name }"
          @click="applyFilter(filter.name)"
        >
          <div class="filter-preview" :style="{ filter: filter.css }">
            <img :src="thumbUrl" alt="">
          </div>
          <div class="filter-name">{{ filter.label }}</div>
        </div>
      </div>

      <!-- 滤镜强度 -->
      <div v-if="currentFilter !== 'none'" class="option-group">
        <label class="option-label">滤镜强度: <span class="value">{{ filterIntensity }}%</span></label>
        <input type="range" v-model.number="filterIntensity" min="0" max="100" class="range-input" @input="updatePreview">
      </div>

      <!-- 自定义调节 -->
      <div class="option-title" style="margin-top: 16px;">自定义调节</div>
      
      <div class="adjust-grid">
        <div class="adjust-item">
          <label>亮度</label>
          <input type="range" v-model.number="adjustments.brightness" min="0" max="200" @input="updatePreview">
          <span>{{ adjustments.brightness }}%</span>
        </div>
        <div class="adjust-item">
          <label>对比度</label>
          <input type="range" v-model.number="adjustments.contrast" min="0" max="200" @input="updatePreview">
          <span>{{ adjustments.contrast }}%</span>
        </div>
        <div class="adjust-item">
          <label>饱和度</label>
          <input type="range" v-model.number="adjustments.saturate" min="0" max="200" @input="updatePreview">
          <span>{{ adjustments.saturate }}%</span>
        </div>
        <div class="adjust-item">
          <label>色相</label>
          <input type="range" v-model.number="adjustments.hueRotate" min="0" max="360" @input="updatePreview">
          <span>{{ adjustments.hueRotate }}°</span>
        </div>
        <div class="adjust-item">
          <label>模糊</label>
          <input type="range" v-model.number="adjustments.blur" min="0" max="10" step="0.5" @input="updatePreview">
          <span>{{ adjustments.blur }}px</span>
        </div>
      </div>

      <button class="btn-reset-adjust" @click="resetAdjustments">重置调节</button>

      <!-- 叠加图层 -->
      <div class="option-title" style="margin-top: 16px;">叠加图层（PNG）</div>
      
      <div class="overlay-section">
        <label class="overlay-upload" v-if="!overlayImage">
          <span>+ 上传 PNG 图片</span>
          <input type="file" accept="image/png" @change="handleOverlayUpload">
        </label>
        
        <div v-if="overlayImage" class="overlay-preview">
          <img :src="overlayImage" alt="叠加图层">
          <button class="btn-remove-overlay" @click="removeOverlay">×</button>
        </div>
        
        <div v-if="overlayImage" class="overlay-options">
          <div class="adjust-item">
            <label>不透明度</label>
            <input type="range" v-model.number="overlayOpacity" min="0" max="100" @input="updatePreview">
            <span>{{ overlayOpacity }}%</span>
          </div>
          <div class="blend-mode">
            <label>混合模式:</label>
            <select v-model="overlayBlendMode" @change="updatePreview">
              <option value="normal">正常</option>
              <option value="multiply">正片叠底</option>
              <option value="screen">滤色</option>
              <option value="overlay">叠加</option>
              <option value="soft-light">柔光</option>
              <option value="hard-light">强光</option>
              <option value="color-dodge">颜色减淡</option>
              <option value="color-burn">颜色加深</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div v-if="hasGif" class="action-row">
      <button class="btn-primary" :disabled="isProcessing" @click="startProcess">
        {{ isProcessing ? '正在处理...' : '生成 GIF' }}
      </button>
      <button class="btn-download" :disabled="!resultGifUrl" @click="downloadGif">下载 GIF</button>
    </div>

    <!-- 进度条 -->
    <div class="progress-container" v-if="isProcessing">
      <div class="progress-label">正在应用滤镜... {{ processProgress }}%</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: processProgress + '%' }"></div>
      </div>
    </div>

    <!-- 结果预览 -->
    <div v-if="resultGifUrl" class="result-section">
      <div class="result-title">处理结果</div>
      <img :src="resultGifUrl" alt="滤镜后的 GIF" class="result-gif">
    </div>
  </div>
</template>

<script>
import SuperGif from '../lib/libgif.js'
import GIF from 'gif.js'
import LoadingTip from './LoadingTip.vue'
import { isMobile, getDeviceConfig, checkFileSize, formatFileSize, yieldToMain, downloadFile } from './utils.js'

export default {
  name: 'GifFilter',
  components: {
    LoadingTip
  },
  data() {
    const config = getDeviceConfig()
    return {
      hasGif: false,
      isDragging: false,
      gifUrl: '',
      gifName: '',
      thumbUrl: '',
      frames: [],
      frameDelays: [],
      originalWidth: 0,
      originalHeight: 0,
      
      // 流式处理
      superGif: null,
      gifWrapper: null,
      blobUrl: null,
      totalFrames: 0,
      
      // 滤镜
      filters: [
        { name: 'none', label: '原图', css: 'none' },
        { name: 'grayscale', label: '黑白', css: 'grayscale(100%)' },
        { name: 'sepia', label: '复古', css: 'sepia(100%)' },
        { name: 'vintage', label: '怀旧', css: 'sepia(50%) contrast(90%) brightness(90%)' },
        { name: 'warm', label: '暖色', css: 'sepia(30%) saturate(140%)' },
        { name: 'cool', label: '冷色', css: 'saturate(80%) hue-rotate(180deg)' },
        { name: 'dramatic', label: '戏剧', css: 'contrast(150%) saturate(120%)' },
        { name: 'fade', label: '褪色', css: 'saturate(50%) brightness(110%)' },
        { name: 'vivid', label: '鲜艳', css: 'saturate(180%) contrast(110%)' },
        { name: 'invert', label: '反色', css: 'invert(100%)' },
        { name: 'blur', label: '模糊', css: 'blur(2px)' },
        { name: 'sharpen', label: '锐化', css: 'contrast(120%)' }
      ],
      currentFilter: 'none',
      filterIntensity: 100,
      
      // 自定义调节
      adjustments: {
        brightness: 100,
        contrast: 100,
        saturate: 100,
        hueRotate: 0,
        blur: 0
      },
      
      // 叠加图层
      overlayImage: null,
      overlayOpacity: 50,
      overlayBlendMode: 'normal',
      
      // 动画
      currentFrameIndex: 0,
      animationTimer: null,
      isPlaying: true,
      
      // 处理状态
      isProcessing: false,
      processProgress: 0,
      resultGifUrl: '',
      
      // 加载状态
      isLoading: false,
      loadingText: '',
      loadingSubtext: '',
      loadingProgress: -1,
      
      // 设备配置
      isMobile: config.isMobile,
      maxGifSize: config.maxGifSize,
      batchSize: config.batchSize
    }
  },
  methods: {
    handleDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file && file.type === 'image/gif') {
        this.processFile(file)
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    processFile(file) {
      // 文件大小检查
      const check = checkFileSize(file, this.maxGifSize)
      if (!check.valid) {
        this.$toast(check.message)
        return
      }
      
      this.gifUrl = URL.createObjectURL(file)
      this.gifName = file.name.replace(/\.gif$/i, '')
      this.hasGif = true
      this.resultGifUrl = ''
      
      // 显示加载提示
      this.isLoading = true
      this.loadingText = '正在解析 GIF...'
      this.loadingSubtext = `文件大小: ${formatFileSize(file.size)}`
      this.loadingProgress = -1
      
      this.parseGifStream(file)
    },
    parseGifStream(file) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:fixed;left:-9999px;top:-9999px;'
      
      const gifImg = document.createElement('img')
      const blobUrl = URL.createObjectURL(file)
      gifImg.setAttribute('rel:animated_src', blobUrl)
      gifImg.setAttribute('rel:auto_play', '0')
      
      wrapper.appendChild(gifImg)
      document.body.appendChild(wrapper)
      
      const rub = new SuperGif({ gif: gifImg })
      rub.load(() => {
        this.totalFrames = rub.get_length()
        this.frames = []
        this.frameDelays = []
        
        const canvas = rub.get_canvas()
        this.originalWidth = canvas.width
        this.originalHeight = canvas.height
        
        const framesData = rub.get_frames()
        
        // 对于预览，只存储少量帧
        const maxPreviewFrames = this.isMobile ? 30 : 50
        const step = Math.max(1, Math.floor(this.totalFrames / maxPreviewFrames))
        
        for (let i = 0; i < this.totalFrames; i++) {
          rub.move_to(i + 1)
          
          let delay = 100
          if (framesData && framesData[i] && framesData[i].delay) {
            delay = framesData[i].delay * 10
          }
          this.frameDelays.push(delay < 20 ? 100 : delay)
          
          // 只存储预览帧
          if (i % step === 0 || i === 0) {
            const frameCanvas = rub.get_canvas()
            const tempCanvas = document.createElement('canvas')
            tempCanvas.width = frameCanvas.width
            tempCanvas.height = frameCanvas.height
            const ctx = tempCanvas.getContext('2d')
            ctx.drawImage(frameCanvas, 0, 0)
            this.frames.push({
              index: i,
              dataUrl: tempCanvas.toDataURL('image/png')
            })
            tempCanvas.width = 1
            tempCanvas.height = 1
          }
        }
        
        // 缩略图
        this.thumbUrl = this.frames[0] ? this.frames[0].dataUrl : ''
        
        // 保存 SuperGif 实例
        this.superGif = rub
        this.gifWrapper = wrapper
        this.blobUrl = blobUrl
        
        this.isLoading = false
        console.log(`GIF 解析完成: ${this.totalFrames} 帧, 预览帧: ${this.frames.length}`)
        
        this.$nextTick(() => {
          this.startAnimation()
        })
      })
    },
    startAnimation() {
      this.stopAnimation()
      if (!this.frames.length) return
      this.currentFrameIndex = 0
      this.isPlaying = true
      this.playNextFrame()
    },
    stopAnimation() {
      if (this.animationTimer) {
        clearTimeout(this.animationTimer)
        this.animationTimer = null
      }
    },
    playNextFrame() {
      if (!this.isPlaying || !this.frames.length) return
      this.renderFrame(this.currentFrameIndex)
      const delay = this.frameDelays[this.currentFrameIndex] || 100
      this.currentFrameIndex = (this.currentFrameIndex + 1) % this.frames.length
      this.animationTimer = setTimeout(() => {
        this.playNextFrame()
      }, delay)
    },
    getFilterCSS() {
      let filterStr = ''
      
      // 预设滤镜
      if (this.currentFilter !== 'none') {
        const filter = this.filters.find(f => f.name === this.currentFilter)
        if (filter) {
          // 根据强度调整滤镜
          filterStr = this.adjustFilterIntensity(filter.css, this.filterIntensity)
        }
      }
      
      // 自定义调节
      if (this.adjustments.brightness !== 100) {
        filterStr += ` brightness(${this.adjustments.brightness}%)`
      }
      if (this.adjustments.contrast !== 100) {
        filterStr += ` contrast(${this.adjustments.contrast}%)`
      }
      if (this.adjustments.saturate !== 100) {
        filterStr += ` saturate(${this.adjustments.saturate}%)`
      }
      if (this.adjustments.hueRotate !== 0) {
        filterStr += ` hue-rotate(${this.adjustments.hueRotate}deg)`
      }
      if (this.adjustments.blur > 0) {
        filterStr += ` blur(${this.adjustments.blur}px)`
      }
      
      return filterStr.trim() || 'none'
    },
    adjustFilterIntensity(css, intensity) {
      if (intensity === 100) return css
      if (intensity === 0) return 'none'
      
      // 对于简单滤镜，调整百分比
      return css.replace(/(\d+)(%|\))/g, (match, num, suffix) => {
        const adjusted = Math.round(parseFloat(num) * intensity / 100)
        return adjusted + suffix
      })
    },
    renderFrame(index) {
      const canvas = this.$refs.previewCanvas
      const frameInfo = this.frames[index]
      if (!canvas || !frameInfo) return
      
      canvas.width = this.originalWidth
      canvas.height = this.originalHeight
      const ctx = canvas.getContext('2d')
      
      const img = new Image()
      img.onload = () => {
        // 应用滤镜
        ctx.filter = this.getFilterCSS()
        ctx.drawImage(img, 0, 0)
        ctx.filter = 'none'
        
        // 应用叠加图层
        if (this.overlayImage && this.overlayImageObj) {
          ctx.globalAlpha = this.overlayOpacity / 100
          ctx.globalCompositeOperation = this.overlayBlendMode
          ctx.drawImage(this.overlayImageObj, 0, 0, this.originalWidth, this.originalHeight)
          ctx.globalAlpha = 1
          ctx.globalCompositeOperation = 'source-over'
        }
      }
      img.src = frameInfo.dataUrl
    },
    updatePreview() {
      // 动画会自动更新
    },
    applyFilter(name) {
      this.currentFilter = name
    },
    resetAdjustments() {
      this.adjustments = {
        brightness: 100,
        contrast: 100,
        saturate: 100,
        hueRotate: 0,
        blur: 0
      }
      this.currentFilter = 'none'
      this.filterIntensity = 100
    },
    handleOverlayUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      
      const url = URL.createObjectURL(file)
      this.overlayImage = url
      
      // 预加载图片对象
      const img = new Image()
      img.onload = () => {
        this.overlayImageObj = img
      }
      img.src = url
    },
    removeOverlay() {
      if (this.overlayImage) {
        URL.revokeObjectURL(this.overlayImage)
      }
      this.overlayImage = null
      this.overlayImageObj = null
    },
    async startProcess() {
      if (!this.superGif || this.totalFrames === 0) {
        this.$toast('请先上传 GIF')
        return
      }
      
      this.isProcessing = true
      this.processProgress = 0
      this.resultGifUrl = ''
      
      const gif = new GIF({
        workers: this.isMobile ? 1 : 2,
        quality: this.isMobile ? 15 : 10,
        workerScript: './static/gifjs/gif.worker.js',
        width: this.originalWidth,
        height: this.originalHeight
      })
      
      gif.on('progress', (p) => {
        this.processProgress = Math.round(50 + p * 50)
      })
      
      // 创建可复用的 canvas
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = this.originalWidth
      tempCanvas.height = this.originalHeight
      const tempCtx = tempCanvas.getContext('2d')
      
      gif.on('finished', async (blob) => {
        // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
        this.resultGifUrl = await blobToBase64(blob)
        this.isProcessing = false
        this.processProgress = 100
        
        // 释放 canvas 内存
        tempCanvas.width = 1
        tempCanvas.height = 1
      })
      
      // 流式处理每一帧
      for (let i = 0; i < this.totalFrames; i++) {
        this.superGif.move_to(i + 1)
        const frameCanvas = this.superGif.get_canvas()
        
        // 应用滤镜
        tempCtx.filter = this.getFilterCSS()
        tempCtx.drawImage(frameCanvas, 0, 0)
        tempCtx.filter = 'none'
        
        // 应用叠加图层
        if (this.overlayImage && this.overlayImageObj) {
          tempCtx.globalAlpha = this.overlayOpacity / 100
          tempCtx.globalCompositeOperation = this.overlayBlendMode
          tempCtx.drawImage(this.overlayImageObj, 0, 0, this.originalWidth, this.originalHeight)
          tempCtx.globalAlpha = 1
          tempCtx.globalCompositeOperation = 'source-over'
        }
        
        const delay = this.frameDelays[i] || 100
        gif.addFrame(tempCanvas, { copy: true, delay: delay })
        
        this.processProgress = Math.round((i + 1) / this.totalFrames * 50)
        
        // 每处理几帧让出主线程
        if (i % this.batchSize === 0) {
          await yieldToMain()
        }
      }
      
      gif.render()
    },
    async downloadGif() {
      if (!this.resultGifUrl) return
      await downloadFile(this.resultGifUrl, `${this.gifName}_filtered.gif`)
    },
    resetAll() {
      this.stopAnimation()
      if (this.gifUrl) URL.revokeObjectURL(this.gifUrl)
      if (this.resultGifUrl) URL.revokeObjectURL(this.resultGifUrl)
      if (this.gifWrapper && this.gifWrapper.parentNode) {
        this.gifWrapper.parentNode.removeChild(this.gifWrapper)
      }
      if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
      this.removeOverlay()
      
      this.hasGif = false
      this.gifUrl = ''
      this.gifName = ''
      this.thumbUrl = ''
      this.frames = []
      this.frameDelays = []
      this.superGif = null
      this.gifWrapper = null
      this.blobUrl = null
      this.totalFrames = 0
      this.resultGifUrl = ''
      this.isProcessing = false
      this.processProgress = 0
      this.currentFilter = 'none'
      this.filterIntensity = 100
      this.isLoading = false
      this.resetAdjustments()
      
      if (this.$refs.gifInput) {
        this.$refs.gifInput.value = ''
      }
    }
  },
  beforeDestroy() {
    this.stopAnimation()
    if (this.gifUrl) URL.revokeObjectURL(this.gifUrl)
    if (this.resultGifUrl) URL.revokeObjectURL(this.resultGifUrl)
    if (this.gifWrapper && this.gifWrapper.parentNode) {
      this.gifWrapper.parentNode.removeChild(this.gifWrapper)
    }
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
    this.removeOverlay()
  }
}
</script>

<style>
.filter-wrapper {
  padding: 8px 0;
}

.filter-wrapper .section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2933;
}

.filter-wrapper .drop-area {
  display: flex;
  flex-direction: column;
  padding: 60px 30px;
  min-height: 180px;
  border: 2px dashed #9fb3ff;
  border-radius: 12px;
  background: #f8f9ff;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: #4f46e5;
  font-weight: 500;
}

.filter-wrapper .drop-area .size-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
  font-weight: 400;
}

.filter-wrapper .drop-area.hover {
  border-color: #667eea;
  background: #e5edff;
}

.filter-wrapper .drop-area input[type="file"] {
  display: none;
}

.filter-wrapper .preview-section {
  background: #f8f9ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.filter-wrapper .preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.filter-wrapper .preview-title,
.filter-wrapper .result-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.filter-wrapper .btn-reselect {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
}

.filter-wrapper .preview-canvas-wrapper {
  text-align: center;
}

.filter-wrapper .preview-canvas {
  max-width: 100%;
  max-height: 220px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.filter-wrapper .filter-options {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.filter-wrapper .option-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.filter-wrapper .filter-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.filter-wrapper .filter-item {
  cursor: pointer;
  text-align: center;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 4px;
  transition: all 0.2s;
}

.filter-wrapper .filter-item:hover {
  border-color: #9fb3ff;
}

.filter-wrapper .filter-item.active {
  border-color: #667eea;
  background: #f0f4ff;
}

.filter-wrapper .filter-preview {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
}

.filter-wrapper .filter-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.filter-wrapper .filter-name {
  font-size: 11px;
  color: #4b5563;
  margin-top: 4px;
}

.filter-wrapper .option-group {
  margin-bottom: 12px;
}

.filter-wrapper .option-label {
  display: block;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 6px;
}

.filter-wrapper .option-label .value {
  color: #667eea;
  font-weight: 600;
}

.filter-wrapper .range-input {
  width: 100%;
}

.filter-wrapper .adjust-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.filter-wrapper .adjust-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-wrapper .adjust-item label {
  width: 60px;
  font-size: 12px;
  color: #4b5563;
  flex-shrink: 0;
}

.filter-wrapper .adjust-item input[type="range"] {
  flex: 1;
}

.filter-wrapper .adjust-item span {
  width: 50px;
  font-size: 12px;
  color: #667eea;
  text-align: right;
}

.filter-wrapper .btn-reset-adjust {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
}

.filter-wrapper .overlay-section {
  margin-top: 8px;
}

.filter-wrapper .overlay-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
}

.filter-wrapper .overlay-upload input {
  display: none;
}

.filter-wrapper .overlay-preview {
  position: relative;
  display: inline-block;
}

.filter-wrapper .overlay-preview img {
  max-width: 100px;
  max-height: 80px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.filter-wrapper .btn-remove-overlay {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: #dc3545;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.filter-wrapper .overlay-options {
  margin-top: 12px;
}

.filter-wrapper .blend-mode {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.filter-wrapper .blend-mode label {
  font-size: 12px;
  color: #4b5563;
}

.filter-wrapper .blend-mode select {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
}

.filter-wrapper .action-row {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}

.filter-wrapper .btn-primary {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.filter-wrapper .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.filter-wrapper .btn-download {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #28a745;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.filter-wrapper .btn-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.filter-wrapper .progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.filter-wrapper .progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.filter-wrapper .progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.filter-wrapper .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.filter-wrapper .result-section {
  margin-top: 16px;
  text-align: center;
}

.filter-wrapper .result-title {
  margin-bottom: 12px;
}

.filter-wrapper .result-gif {
  max-width: 100%;
  max-height: 250px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}
</style>

